import { useEffect } from 'react'
import {
  getDisplayName,
  normalizeAgentName,
  AGENT_STORE_BG_COLORS,
  DEFAULT_AGENT_BG_COLOR,
} from '@/constants/agentNames'
import { useAppStore } from '@/store'

// Map store agent IDs to canonical chat agent IDs used by setActiveAgent
const STORE_TO_CANONICAL: Record<string, string> = {
  'plant-support': 'plant_support_engineer',
  'chemical-agent': 'chemical_engineer',
  'mechanical-agent': 'mechanical_engineer',
  'electrical-agent': 'electrical_engineer',
}

interface EmptyChatStateV2Props {
  activeAgent?: string
  onAgentSelect?: (agentId: string) => void
}

export default function EmptyChatStateV2({ activeAgent, onAgentSelect }: EmptyChatStateV2Props) {
  const { setActiveAgent, agents, fetchAgents } = useAppStore()
  const currentAgent = activeAgent || 'plant_support_engineer'

  // Ensure agents are loaded. Guarded because component tests that mock
  // useAppStore via plain mockReturnValue don't always seed `fetchAgents`,
  // and an undefined call here surfaces as `fetchAgents is not a function`.
  useEffect(() => {
    fetchAgents?.()
  }, [fetchAgents])

  const handleSelect = (storeAgentId: string) => {
    const canonicalId = STORE_TO_CANONICAL[storeAgentId] || storeAgentId
    setActiveAgent(canonicalId)
    onAgentSelect?.(canonicalId)
  }

  return (
    <div className="flex flex-col items-center justify-center h-full px-4 py-8 gap-6">
      <h2 className="text-xl font-semibold text-foreground">
        How can I help you today?
      </h2>

      {/* Agent grid — only enabled agents from the store */}
      <div className="flex flex-wrap justify-center gap-4">
        {agents.map((agent) => {
          const canonicalId = STORE_TO_CANONICAL[agent.id] || agent.id
          const displayName = agent.name || getDisplayName(canonicalId)
          const isActive = normalizeAgentName(canonicalId) === normalizeAgentName(currentAgent)
          const bgColor = AGENT_STORE_BG_COLORS[agent.id] || DEFAULT_AGENT_BG_COLOR
          return (
            <button
              key={agent.id}
              type="button"
              onClick={() => handleSelect(agent.id)}
              className="flex flex-col items-center gap-1.5 w-[90px] group"
            >
              <div className="relative">
                <div
                  className={`w-14 h-14 rounded-full overflow-hidden border-2 transition-colors ${
                    isActive ? 'border-primary' : 'border-transparent group-hover:border-gray-300'
                  }`}
                  style={{ backgroundColor: bgColor }}
                >
                  <img
                    src={agent.image}
                    alt={displayName}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                </div>
                {/* Online dot */}
                {agent.isOnline && (
                  <div className="absolute bottom-0.5 right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white" />
                )}
              </div>
              <span className={`text-xs text-center leading-tight ${
                isActive ? 'font-medium text-primary' : 'text-muted-foreground'
              }`}>
                {displayName}
              </span>
            </button>
          )
        })}
      </div>

    </div>
  )
}
